/*
 * Idle sign-out bookkeeping, shared by every tab of this browser through localStorage, so
 * activity in any tab keeps them all signed in. Ported from PelicanBlue's AutoStow web app.
 *
 * Storage that is refused (a private window) falls back to this tab's memory; a session with
 * no recorded activity yet (the first load after this shipped) counts from when the page
 * loaded, never from 1970.
 */

export const LAST_ACTIVITY_KEY = "pbo.lastActivity";
/** The minutes, set when the app signed the director out for inactivity, for the login page. */
const IDLE_SIGN_OUT_KEY = "pbo.idleSignOut";

let lastActivityHere = Date.now();

/** When the director was last active in any tab (epoch ms). */
export function readLastActivity(): number {
  try {
    const time = Number.parseInt(localStorage.getItem(LAST_ACTIVITY_KEY) ?? "", 10);
    if (Number.isFinite(time)) return time;
  } catch {
    /* storage refused: this tab's own record below */
  }
  return lastActivityHere;
}

export function recordActivity(now = Date.now()): void {
  lastActivityHere = now;
  try {
    localStorage.setItem(LAST_ACTIVITY_KEY, String(now));
  } catch {
    /* storage refused: kept in this tab only */
  }
}

export function noteIdleSignOut(minutes: number): void {
  try {
    localStorage.setItem(IDLE_SIGN_OUT_KEY, String(minutes));
  } catch {
    /* no notice on the login page; the sign-out itself still happens */
  }
}

/** The login page's notice after an idle sign-out, or null. */
export function idleSignOutNotice(): string | null {
  try {
    const minutes = localStorage.getItem(IDLE_SIGN_OUT_KEY);
    if (minutes === null) return null;
    return `You were signed out after ${minutes} ${minutes === "1" ? "minute" : "minutes"} without activity.`;
  } catch {
    return null;
  }
}

/** The login page calls it once it has shown the notice, so a reload does not repeat it. */
export function clearIdleSignOutNotice(): void {
  try {
    localStorage.removeItem(IDLE_SIGN_OUT_KEY);
  } catch {
    /* nothing stored */
  }
}

/** A fresh sign-in: drop the notice and start the clock now, so last night's activity time
 * does not sign the director straight out again. */
export function startIdleClock(): void {
  clearIdleSignOutNotice();
  recordActivity();
}
